import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import * as styles from './FoodTable.styles';
import { Food, FoodItem } from '../../userMenu.types';
import Toast from '../../../../components/Toast/Toast';
import { DraggableRow } from './DraggableRow';
import { LANG_TEXT } from '../../../../constants/language';
import { useUserMenuContext } from '../../../../context/UserMenuContext';

interface FoodTableProps {
  onSaveRef: RefObject<(() => Promise<void>) | null>;
  onCancelRef: RefObject<(() => void) | null>;
  onChangesDetected: (hasChanges: boolean) => void;
}

interface CategoryPendingState {
  foodItems: FoodItem[];
  recommendationsMap: Record<string, FoodItem[]>;
  initialFoodItems: FoodItem[];
  initialRecommendationsMap: Record<string, FoodItem[]>;
}

export function FoodTable({ onSaveRef, onCancelRef, onChangesDetected }: FoodTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getFoodsByCategoryId, getFoodById, activeCategoryId, loading, updateUserMenuCategory, maxBannerQuantity, maxRecommendQuantity } = useUserMenuContext();
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [recommendationsVersion, setRecommendationsVersion] = useState(0);
  const [openToast, setOpenToast] = useState(false);
  const toastData = useRef<{ type: 'error' | 'success'; content: string }>({ type: 'error', content: '' });
  const initialFoodItemsRef = useRef<FoodItem[]>([]);
  const initialRecommendationsMapRef = useRef<Record<string, FoodItem[]>>({});
  const recommendationsMapRef = useRef<Record<string, FoodItem[]>>({});

  const pendingMapRef = useRef<Record<string, CategoryPendingState>>({});
  const prevCategoryIdRef = useRef<string | null>(null);
  const foodItemsRef = useRef<FoodItem[]>([]);
  useEffect(() => {
    foodItemsRef.current = foodItems;
  }, [foodItems]);

  const currentTabHasChanges = (): boolean => {
    const bannerOrActiveChanged = foodItemsRef.current.some((item) => {
      const initialItem = initialFoodItemsRef.current.find((init) => init.id === item.id);
      return initialItem && (item.banner !== initialItem.banner || item.enabled !== initialItem.enabled || item.order !== initialItem.order);
    });
    const recsChanged = JSON.stringify(recommendationsMapRef.current) !== JSON.stringify(initialRecommendationsMapRef.current);
    return bannerOrActiveChanged || recsChanged;
  };


  useEffect(() => {
    if (loading) return;

    const prevId = prevCategoryIdRef.current;
    if (prevId && prevId !== activeCategoryId) {
      if (currentTabHasChanges()) {
        pendingMapRef.current[prevId] = {
          foodItems: [...foodItemsRef.current],
          recommendationsMap: { ...recommendationsMapRef.current },
          initialFoodItems: [...initialFoodItemsRef.current],
          initialRecommendationsMap: { ...initialRecommendationsMapRef.current },
        };
      } else {
        delete pendingMapRef.current[prevId];
      }
    }
    prevCategoryIdRef.current = activeCategoryId ?? null;

    if (!activeCategoryId) {
      setFoodItems([]);
      return;
    }

    const cached = pendingMapRef.current[activeCategoryId];
    if (cached) {
      setFoodItems(cached.foodItems);
      recommendationsMapRef.current = { ...cached.recommendationsMap };
      initialFoodItemsRef.current = cached.initialFoodItems;
      initialRecommendationsMapRef.current = cached.initialRecommendationsMap;
      setRecommendationsVersion((v) => v + 1);
      onChangesDetected(true);
      return;
    }

    const foods = getFoodsByCategoryId(activeCategoryId);
    const mappedFoods: FoodItem[] = foods
      .filter((f: Food) => {
        const categoryMenuIds = Array.isArray(f.categories_menu_ids) ? f.categories_menu_ids : [];
        return categoryMenuIds.includes(activeCategoryId);
      })
      .map((f: Food) => {
        return {
          id: f.id,
          name: f.name ?? f.food_name ?? '',
          store: f.restaurantName || f.sku || '',
          image: Array.isArray(f.image_url) ? f.image_url[0] : f.image_url,
          price: f.price,
          banner: f?.banner ?? false,
          banner_order: f?.banner_order,
          enabled: f?.enabled ?? true,
          order: f?.order,
          restaurantId: f.restaurantId || '',
          food_enabled: f.food_enabled,
          enabled_pre_order: (f as Food).enabled_pre_order,
        };
      })
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.order !== undefined) {
          return -1;
        }
        if (b.order !== undefined) {
          return 1;
        }
        return 0;
      });

    setFoodItems(mappedFoods);
    initialFoodItemsRef.current = mappedFoods;

    const initialRecs: Record<string, FoodItem[]> = {};
    mappedFoods.forEach((foodItem) => {
      const original = foods.find((f: Food) => f.id === foodItem.id);
      if (original && activeCategoryId) {
        const recMeta = original?.recommended_food ?? [];
        const mapped: FoodItem[] = recMeta
          .map((meta) => {
            const target = getFoodById(meta.recommended_food_id);
            if (!target) return null;
            return {
              id: meta.recommended_food_id,
              name: target.name ?? target.food_name ?? '',
              store: target.sku || target.restaurantName || '',
              image: Array.isArray(target.image_url) ? target.image_url[0] : target.image_url,
              price: target.price,
              banner: false,
              enabled: meta.enabled,
              restaurantId: target.restaurantId || '',
              food_enabled: meta.food_enabled,
            } as FoodItem;
          })
          .filter((x): x is FoodItem => x !== null);
        mapped.sort((a, b) => {
          const ma = recMeta.find((m) => m.recommended_food_id === a.id)?.order ?? 0;
          const mb = recMeta.find((m) => m.recommended_food_id === b.id)?.order ?? 0;
          return ma - mb;
        });
        initialRecs[foodItem.id] = mapped;
      }
    });
    initialRecommendationsMapRef.current = initialRecs;
    recommendationsMapRef.current = { ...initialRecs };
    setRecommendationsVersion(0);
    const cachedHasChanges = Object.keys(pendingMapRef.current).length > 0;
    onChangesDetected(false || cachedHasChanges);
  }, [getFoodsByCategoryId, getFoodById, activeCategoryId, loading, onChangesDetected]);

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    const dragItem = foodItems[dragIndex];
    const newItems = [...foodItems];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, dragItem);
    const reindexed = newItems.map((it, idx) => ({ ...it, order: idx }));
    setFoodItems(reindexed);
  };

  useEffect(() => {
    const bannerOrActiveChanged = foodItems.some((item) => {
      const initialItem = initialFoodItemsRef.current.find((init) => init.id === item.id);
      return initialItem && (item.banner !== initialItem.banner || item.banner_order !== initialItem.banner_order || item.enabled !== initialItem.enabled || item.order !== initialItem.order);
    });

    const currentRecsStr = JSON.stringify(recommendationsMapRef.current);
    const initialRecsStr = JSON.stringify(initialRecommendationsMapRef.current);
    const recommendationsChanged = currentRecsStr !== initialRecsStr;

    const hasChanged = bannerOrActiveChanged || recommendationsChanged;
    const cachedHasChanges = Object.keys(pendingMapRef.current).length > 0;
    onChangesDetected(hasChanged || cachedHasChanges);
  }, [foodItems, recommendationsVersion, onChangesDetected]);

  const handleBannerChange = (id: string, checked: boolean) => {
    if (checked && maxBannerQuantity !== null) {
      const currentBannerCount = foodItems.filter(i => i.banner).length;
      if (currentBannerCount >= maxBannerQuantity) {
        toastData.current = {
          type: 'error',
          content: t(LANG_TEXT.MAX_BANNER_LIMIT)
        };
        setOpenToast(true);
        return;
      }
    }
    setFoodItems((prev) => {
      if (checked) {
        // Assign next banner_order
        const maxOrder = prev.filter((i) => i.banner).reduce((max, i) => Math.max(max, i.banner_order ?? 0), 0);
        return prev.map((item) => item.id === id ? { ...item, banner: true, banner_order: maxOrder + 1 } : item);
      } else {
        // Remove and re-sequence remaining banners
        const removed = prev.map((item) => item.id === id ? { ...item, banner: false, banner_order: undefined } : item);
        let seq = 1;
        return removed.map((item) => {
          if (!item.banner) return item;
          return { ...item, banner_order: seq++ };
        });
      }
    });
  };

  const handleActiveChange = (id: string, checked: boolean) => {
    setFoodItems((prev) => prev.map((item) => (item.id === id ? { ...item, enabled: checked } : item)));
  };

  const handleRecommendationsChange = (foodId: string, recommendations: FoodItem[]) => {
    recommendationsMapRef.current[foodId] = recommendations;
    setRecommendationsVersion((prev) => prev + 1);
  };

  const handleSave = async () => {
    if (!activeCategoryId) return;

    try {
      const allToSave: Record<string, { foodItems: FoodItem[]; recommendationsMap: Record<string, FoodItem[]> }> = {};

      for (const [catId, pending] of Object.entries(pendingMapRef.current)) {
        allToSave[catId] = {
          foodItems: pending.foodItems,
          recommendationsMap: pending.recommendationsMap,
        };
      }

      allToSave[activeCategoryId] = {
        foodItems: foodItemsRef.current,
        recommendationsMap: { ...recommendationsMapRef.current },
      };

      for (const [catId, state] of Object.entries(allToSave)) {
        await updateUserMenuCategory(catId, state.foodItems, state.recommendationsMap);
      }

      pendingMapRef.current = {};
      initialFoodItemsRef.current = [...foodItemsRef.current];
      initialRecommendationsMapRef.current = { ...recommendationsMapRef.current };
      setRecommendationsVersion(0);
      onChangesDetected(false);
    } catch (error) {
      console.error('Error saving changes:', error);
    }
  };

  const handleCancel = () => {
    pendingMapRef.current = {};
    setFoodItems([...initialFoodItemsRef.current]);
    recommendationsMapRef.current = { ...initialRecommendationsMapRef.current };
    setRecommendationsVersion(0);
    onChangesDetected(false);
    window.dispatchEvent(new Event('recommendations-restore'));
  };

  useEffect(() => {
    onSaveRef.current = handleSave;
    onCancelRef.current = handleCancel;
  }, [handleSave, handleCancel, onSaveRef, onCancelRef]);

  const handleEdit = (id: string) => {
    const item = foodItems.find((f) => f.id === id);
    if (!item?.restaurantId) return;
    navigate(`/food_trucks/${item.restaurantId}/menu_management`, { state: { editFoodId: id } });
  };

  return (
    <>
      <Toast content={toastData.current?.content} variant={toastData.current?.type} open={openToast} onClose={() => setOpenToast(false)} />
      <TableContainer sx={styles.tableContainerStyles}>
        <Table stickyHeader sx={styles.tableStyles}>
          <colgroup>
            <col style={{ width: '9.4%' }} />
            <col style={{ width: '6.9%' }} />
            <col style={{ width: '38.8%' }} />
            <col style={{ width: '12.8%' }} />
            <col style={{ width: '11.7%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '9.4%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCellStyles}></TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.RECO)}</Typography>
              </TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.MENU)}</Typography>
              </TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.PRICE)}</Typography>
              </TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.BANNER)}</Typography>
              </TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.ON_OFF)}</Typography>
              </TableCell>
              <TableCell sx={styles.headerCellStyles}>
                <Typography sx={styles.headerTypographyStyles}>{t(LANG_TEXT.EDIT)}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {foodItems.map((item, index) => (
              <DraggableRow
                key={item.id}
                item={item}
                index={index}
                moveRow={moveRow}
                handleBannerChange={handleBannerChange}
                handleActiveChange={handleActiveChange}
                handleEdit={handleEdit}
                onRecommendationsChange={handleRecommendationsChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
